import React from 'react';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { toastr } from 'react-redux-toastr';
import {
  Modal,
  ModalConfirm,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Button,
  SubmitButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import ForecastRentsUtilityAllowanceFormService from '../../../services/forecastRentsUtilityAllowanceFormService';

import UtilityAllowanceTable from './table';
import rentForecastMessages from '../messages';
import messages from './messages';
import useIntl from '../../../hooks/useIntl';
import { factorToPercent } from '../hooks.useRows';

const UtilityAllowanceTableModal = (props) => {
  const {
    open,
    closeModal,
    rentForecasts, // from API query
    currentRentForecast,
    rentForecastTableRows,
    setProposedState,
    canEdit,
  } = props;

  const intl = useIntl();

  const useFormReturn = useForm({
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { dirtyFields, errors },
  } = useFormReturn;

  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();

  const handleDiscardClick = React.useCallback(() => {
    closeConfirmModal();
    closeModal();
  }, [closeConfirmModal, closeModal]);

  const currentForecastId = currentRentForecast?.id;
  const proposedChangeType = currentRentForecast?.proposedChangeType;

  React.useEffect(() => {
    if (!open || !currentForecastId) {
      return;
    }
    if (refetchUtilityDataRef.current) {
      setTimeout(() => {
        refetchUtilityDataRef.current();
      }, 100);
    }

    useFormReturn.reset({});

    const initialValues = {};
    rentForecastTableRows.forEach((row) => {
      const value =
        row['newRD Utility AllowanceProposedChangeValue']?.value || 0;

      initialValues[`${row.id}-proposedChange`] =
        proposedChangeType === 'percent' ? factorToPercent(value) : value;
    });

    useFormReturn.reset(initialValues);
  }, [open, currentForecastId, proposedChangeType]);

  const ConfirmModalProps = React.useMemo(
    () => ({
      title: intl.formatMessage(rentForecastMessages.confirmModalTitle),
      open: openConfirm,
      actionsProps: [
        {
          children: intl.formatMessage(rentForecastMessages.cancel),
          onClick: closeConfirmModal,
        },
        {
          children: intl.formatMessage(rentForecastMessages.confirm),
          onClick: handleDiscardClick,
        },
      ],
    }),
    [openConfirm, closeConfirmModal, handleDiscardClick, intl],
  );

  const handleCloseWithoutSave = React.useCallback(() => {
    const isDirty = Object.keys(dirtyFields).length > 0;
    if (isDirty) {
      openConfirmModal();
    } else {
      closeModal();
    }
  }, [dirtyFields, openConfirmModal, closeModal]);

  const updateProposed = (formField) => {
    const proposedValues = _.entries(formField)
      .filter(([field, value]) => field.includes('proposedChange') && !!value)
      .reduce((acc, [field, value]) => {
        const sliceIndex = field.lastIndexOf('-');
        const floorPlanId = field.slice(0, sliceIndex);

        return {
          ...acc,
          [`${floorPlanId}-newRD Utility AllowanceProposedChange`]: value,
        };
      }, {});

    setProposedState((prev) => ({
      ...prev,
      ...proposedValues,
    }));
  };

  // Utility Allowances for New Rent Forecast - 2025
  // const Title = `Utility Allowances for ${currentRentForecast?.name} - ${currentRentForecast?.year}`;

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const refetchUtilityDataRef = React.useRef(null);

  const onSubmit = React.useCallback(
    (formData) => {
      setIsSubmitting(true);
      const forecastRentsUtilityAllowanceFormService =
        new ForecastRentsUtilityAllowanceFormService();
      const forecastRentsUtilityAllowanceFormData = {
        rentForecastId: currentRentForecast?.id,
        formData,
      };

      forecastRentsUtilityAllowanceFormService
        .upsertForecastRentsUtilityAllowanceForm(
          forecastRentsUtilityAllowanceFormData,
        )
        .then(() => {
          toastr.success(
            intl.formatMessage(rentForecastMessages.success),
            intl.formatMessage(messages.utilityAllowanceSaved),
          );
          updateProposed(formData);
          setIsSubmitting(false);
          closeModal();

          setTimeout(() => {
            if (refetchUtilityDataRef.current) {
              refetchUtilityDataRef.current();
              setTimeout(() => {
                refetchUtilityDataRef.current();
              }, 300);
            }
          }, 100);
        })
        .catch((error) => {
          toastr.error(
            intl.formatMessage(rentForecastMessages.error),
            error.message || error.networkError,
            { timeOut: 0, showCloseButton: true },
          );
          setIsSubmitting(false);
        });
    },
    [intl, currentRentForecast?.id, updateProposed, useFormReturn, closeModal],
  );

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseWithoutSave}
        title={intl.formatMessage(messages.title, {
          currentRentForecastName: currentRentForecast?.name,
          currentRentForecastYear: currentRentForecast?.year,
        })}
        actions={
          <>
            <Button variant="text" onClick={handleCloseWithoutSave}>
              {intl.formatMessage(rentForecastMessages.cancel)}
            </Button>
            <SubmitButton
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isSubmitting={isSubmitting}
              disabled={!canEdit || !_.isEmpty(errors)}
            >
              {intl.formatMessage(rentForecastMessages.save)}
            </SubmitButton>
          </>
        }
        ModalProps={{
          PaperProps: {
            sx: {
              width: '100%',
              maxWidth: {
                xs: 'calc(100dvw - 32px)',
                lg: 'xl',
              },
            },
          },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <UtilityAllowanceTable
            useFormReturn={useFormReturn}
            rentForecasts={rentForecasts}
            currentRentForecast={currentRentForecast}
            rentForecastTableRows={rentForecastTableRows}
            canEdit={canEdit}
          />
        </form>
      </Modal>
      <ModalConfirm {...ConfirmModalProps} />
    </>
  );
};

export default UtilityAllowanceTableModal;
